import type { UseFetchOptions } from 'nuxt/app'

interface LogEntry {
  type: 'log' | 'error' | 'warn' | 'request' | 'response'
  message: string
  data?: unknown
  timestamp: number
  correlationId?: string
  method?: string
  url?: string
  status?: number
  duration?: number
}

/**
 * Extended UseFetchOptions with correlation ID for request tracking
 */
type UseFetchOptionsWithCorrelationId<T> = UseFetchOptions<T> & {
  __correlationId?: string
}

/**
 * Sanitizes data to remove non-serializable objects and sensitive fields
 */
const sanitizeData = (data: unknown): unknown => {
  if (data === null || data === undefined) {
    return data
  }

  if (typeof data !== 'object') {
    return data
  }

  if (Array.isArray(data)) {
    return data.map(sanitizeData)
  }

  if (data instanceof Date) {
    return data.toISOString()
  }

  if (data instanceof Error) {
    return {
      name: data.name,
      message: data.message,
      stack: data.stack,
    }
  }

  const sanitized: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(data)) {
    if (key === 'signal' || value instanceof AbortSignal) {
      sanitized[key] = '[AbortSignal]'
      continue
    }

    if (typeof value === 'function') {
      sanitized[key] = `[Function: ${value.name || 'anonymous'}]`
      continue
    }

    // Substring match covers apiKey, accessToken, X-Auth-Token, …
    const sensitiveKeyParts = ['apikey', 'token', 'password', 'authorization', 'auth', 'secret']
    // Exact match for names too generic for that: the API key endpoints return the key itself
    // as `key` (create) and `full` (list), while `publicKey` is only an identifier.
    const sensitiveKeyNames = ['key', 'full', 'pwd', 'passwd']
    const lowerKey = key.toLowerCase()
    if (sensitiveKeyNames.includes(lowerKey) || sensitiveKeyParts.some((part) => lowerKey.includes(part))) {
      sanitized[key] = '[REDACTED]'
      continue
    }

    sanitized[key] = sanitizeData(value)
  }

  return sanitized
}

/**
 * Generates a unique correlation ID for request tracking
 */
const generateCorrelationId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
}

/**
 * Unified composable for request logging with correlation IDs and timing
 */
export const useRequestLogger = () => {
  const logs = useState<LogEntry[]>('client-logs', () => [])
  const requestTimings = new Map<string, number>()

  const addLog = (
    type: LogEntry['type'],
    message: string,
    data?: unknown,
    metadata?: {
      correlationId?: string
      method?: string
      url?: string
      status?: number
      duration?: number
    }
  ) => {
    logs.value.push({
      type,
      message,
      data: data !== undefined ? sanitizeData(data) : undefined,
      timestamp: Date.now(),
      ...metadata,
    })
  }

  // Basic logging methods
  const log = (message: string, data?: unknown) => {
    addLog('log', message, data)
  }

  const error = (message: string, data?: unknown) => {
    addLog('error', message, data)
  }

  const warn = (message: string, data?: unknown) => {
    addLog('warn', message, data)
  }

  // Request logging methods
  const logRequest = (method: string, url: string, data?: unknown, correlationId?: string): string => {
    const id = correlationId || generateCorrelationId()
    addLog('request', `[${method}] ${url}`, data, {
      correlationId: id,
      method,
      url,
    })
    return id
  }

  const logResponse = (
    correlationId: string,
    method: string,
    url: string,
    status: number,
    data?: unknown,
    duration?: number
  ) => {
    addLog('response', `[${method}] ${url} - ${status}`, data, {
      correlationId,
      method,
      url,
      status,
      duration,
    })
  }

  const logError = (
    correlationId: string,
    method: string,
    url: string,
    error: unknown,
    status?: number,
    duration?: number
  ) => {
    addLog('error', `[${method}] ${url}${status ? ` - ${status}` : ''}`, error, {
      correlationId,
      method,
      url,
      status,
      duration,
    })
  }

  // Handlers for useFetch
  const createRequestHandler = <T>(
    method: string,
    url: string,
    extractData?: (options: UseFetchOptions<T>) => unknown
  ) => {
    return ({ options }: { options: UseFetchOptions<T> }) => {
      const correlationId = generateCorrelationId()
      const startTime = Date.now()
      requestTimings.set(correlationId, startTime)

      const requestData = extractData
        ? extractData(options)
        : {
            body: options.body,
            query: options.query,
            headers: options.headers,
          }

      logRequest(method, url, requestData, correlationId)
      ;(options as UseFetchOptionsWithCorrelationId<T>).__correlationId = correlationId
    }
  }

  const createResponseHandler = <T>(method: string, url: string) => {
    return ({ response, options }: { response: { _data?: T; status?: number }; options?: UseFetchOptions<T> }) => {
      const correlationId = (options as UseFetchOptionsWithCorrelationId<T>)?.__correlationId
      if (!correlationId) return

      const startTime = requestTimings.get(correlationId)
      const duration = startTime ? Date.now() - startTime : undefined
      requestTimings.delete(correlationId)

      logResponse(correlationId, method, url, response.status || 200, response._data, duration)
    }
  }

  const createErrorHandler = (method: string, url: string) => {
    return ({ response, options }: { response: { _data?: unknown; status?: number }; options?: unknown }) => {
      const correlationId = (options as { __correlationId?: string })?.__correlationId
      if (!correlationId) return

      const startTime = requestTimings.get(correlationId)
      const duration = startTime ? Date.now() - startTime : undefined
      requestTimings.delete(correlationId)

      logError(correlationId, method, url, response._data || new Error('Unknown error'), response.status, duration)
    }
  }

  // Helpers for $fetch
  const logFetchRequest = (method: string, url: string, data?: unknown) => {
    const correlationId = generateCorrelationId()
    const startTime = Date.now()
    requestTimings.set(correlationId, startTime)

    logRequest(method, url, data, correlationId)

    return { correlationId, startTime }
  }

  const logFetchResponse = (correlationId: string, method: string, url: string, status: number, data?: unknown) => {
    const startTime = requestTimings.get(correlationId)
    const duration = startTime ? Date.now() - startTime : undefined
    requestTimings.delete(correlationId)

    logResponse(correlationId, method, url, status, data, duration)
  }

  const logFetchError = (correlationId: string, method: string, url: string, error: unknown, status?: number) => {
    const startTime = requestTimings.get(correlationId)
    const duration = startTime ? Date.now() - startTime : undefined
    requestTimings.delete(correlationId)

    logError(correlationId, method, url, error, status, duration)
  }

  // Flush logs to console
  const flushLogs = () => {
    if (import.meta.client && logs.value.length > 0) {
      logs.value.forEach((entry) => {
        const parts: string[] = [`[${entry.type.toUpperCase()}]`]

        if (entry.correlationId) {
          parts.push(`[${entry.correlationId}]`)
        }

        if (entry.method && entry.url) {
          parts.push(`${entry.method} ${entry.url}`)
        }

        if (entry.status) {
          parts.push(`→ ${entry.status}`)
        }

        if (entry.duration !== undefined) {
          parts.push(`(${entry.duration}ms)`)
        }

        parts.push(entry.message)

        const prefix = parts.join(' ')
        const logData: Record<string, unknown> = {}

        if (entry.data !== undefined) {
          logData.data = entry.data
        }

        if (entry.correlationId) {
          logData.correlationId = entry.correlationId
        }

        if (entry.duration !== undefined) {
          logData.duration = entry.duration
        }

        switch (entry.type) {
          case 'error':
            if (Object.keys(logData).length > 0) {
              console.error(prefix, logData)
            } else {
              console.error(prefix)
            }
            break
          case 'warn':
            if (Object.keys(logData).length > 0) {
              console.warn(prefix, logData)
            } else {
              console.warn(prefix)
            }
            break
          default:
            if (Object.keys(logData).length > 0) {
              console.log(prefix, logData)
            } else {
              console.log(prefix)
            }
        }
      })

      logs.value = []
    }
  }

  return {
    // Basic logging
    log,
    error,
    warn,
    // Request logging
    logRequest,
    logResponse,
    logError,
    // useFetch handlers
    createRequestHandler,
    createResponseHandler,
    createErrorHandler,
    // $fetch helpers
    logFetchRequest,
    logFetchResponse,
    logFetchError,
    // Utilities
    flushLogs,
    logs: readonly(logs),
    generateCorrelationId,
  }
}
